import type { TaskTypeName } from 'src/gql/graphql';
import { isNil } from 'es-toolkit';
import { GUIDED_FLOW_LINKS, TASK_ID_PARAM_SYNCED_TO_SALESFORCE } from 'src/constants/tasks';

import { TASK_STATUS_DONE } from '@savvy/constants/task-statuses';

import { CLIENT_PORTAL_TASKS_ROUTE } from '../shell/constants';
import { CLIENT_PORTAL_EXCLUDED_TASK_TYPES } from './constants';

const EXCLUDED_TASK_TYPES: readonly string[] = CLIENT_PORTAL_EXCLUDED_TASK_TYPES;

const NO_TASK_IDS: ReadonlySet<string> = new Set();

export type ClientPortalTaskSummary = {
  readonly id: string;
  readonly status: string;
  readonly type: string;
};

export function isOpenClientTask(
  task: ClientPortalTaskSummary,
  ownOnboardingTaskIds: ReadonlySet<string> = NO_TASK_IDS,
): boolean {
  return (
    task.status !== TASK_STATUS_DONE && (!EXCLUDED_TASK_TYPES.includes(task.type) || ownOnboardingTaskIds.has(task.id))
  );
}

export function openClientTasks<T extends ClientPortalTaskSummary>(
  tasks: readonly T[] | undefined,
  ownOnboardingTaskIds: ReadonlySet<string> = NO_TASK_IDS,
): readonly T[] {
  return isNil(tasks) ? [] : tasks.filter((task) => isOpenClientTask(task, ownOnboardingTaskIds));
}

export function isCompletedClientTask(task: ClientPortalTaskSummary): boolean {
  return task.status === TASK_STATUS_DONE && !EXCLUDED_TASK_TYPES.includes(task.type);
}

export function completedClientTasks<T extends ClientPortalTaskSummary>(tasks: readonly T[] | undefined): readonly T[] {
  return isNil(tasks) ? [] : tasks.filter(isCompletedClientTask);
}

export function clientTaskHref(task: {
  readonly id: string;
  readonly status: string;
  readonly type: TaskTypeName;
}): string {
  const detailHref = `${CLIENT_PORTAL_TASKS_ROUTE}?${TASK_ID_PARAM_SYNCED_TO_SALESFORCE}=${task.id}`;
  if (task.status === TASK_STATUS_DONE) {
    return detailHref;
  }
  const guidedFlowHref = GUIDED_FLOW_LINKS[task.type];
  return isNil(guidedFlowHref) ? detailHref : guidedFlowHref;
}
